import { t } from '@avalara/bat-apps-shared/i18n';
import { SAlert, SCol, SRow } from '@avalara/skylab-sdk/react';

import { formatPrice } from '../../../../shared/helpers';
import { getSalestaxDisplayText } from '../../../reducers/helpers';
import Discount from '../order-summary/discount';
import OrderItem from '../order-summary/order-item';
import OrderReviewPriceTable from './price-table';
import OrderReviewQuantityRow from './quantity-row';

import type { TranslationKey } from '@avalara/bat-apps-shared/i18n';
import type { OfferItem } from '@avalara/types/offers';
import type { Currency } from '../../../../../types/currency';
import type { PriceTableRow, ProductPricingTier } from '../../../../../types/product-detail';

import './order-review.css';

type Props = {
  // Optional: the PDP tier that sells this offer might not be found (e.g. the reverse offerFlow
  // lookup finds nothing) — the summary still renders using productItems/total in that case, just
  // without the fine print extra.
  tier?: ProductPricingTier;
  productItems: Array<OfferItem>;
  additionalItems: Array<OfferItem>;
  currency?: Currency;
  discountAmount: number;
  discountName?: string | null;
  salesTax: number | null;
  // False for offers with no taxCode (e.g. Registrations, BRGUI-2581) — tax there is calculated at
  // invoicing rather than estimated at checkout, so this row would otherwise always read
  // "Calculated at checkout" regardless of the offer.
  showSalesTax: boolean;
  // Opt-in — see OfferOrderSummary.showTierFinePrint. Most offers don't set this, so `tier`'s fine
  // print stays PDP-only unless an offer explicitly asks for it here too.
  showTierFinePrint?: boolean;
  total: number;
  adjustableQuantity?: boolean;
  quantity: number;
  preDiscountSubtotal: number;
  minQuantity?: number;
  maxQuantity?: number;
  priceTable?: Array<PriceTableRow>;
  onChangeQuantity: (_: number) => void;
};

const OrderReviewSummary = ({
  tier,
  productItems,
  additionalItems,
  currency,
  discountAmount,
  discountName,
  salesTax,
  showSalesTax,
  showTierFinePrint,
  total,
  adjustableQuantity,
  quantity,
  preDiscountSubtotal,
  minQuantity,
  maxQuantity,
  priceTable,
  onChangeQuantity,
}: Props) => (
  <div data-ref='buy.order-review.summary'>
    <SRow className='margin-bottom-md'>
      <SCol className='pad-bottom-none'>
        <h2 data-ref='buy.order-review.summary.heading'>{t('client.order-review.order-summary')}</h2>
      </SCol>
    </SRow>

    <SAlert
      status='info'
      noDismiss
      id='buy.order-review.summary.info-alert'
      className='product-detail-pricing-disclaimer margin-bottom-md show-icon'
    >
      {t(
        showSalesTax
          ? 'client.order-review.order-summary.info-alert'
          : 'client.order-review.order-summary.info-alert.no-sales-tax',
      )}
    </SAlert>

    {priceTable && priceTable.length > 0 && <OrderReviewPriceTable rows={priceTable} />}

    {adjustableQuantity && productItems[0] != null ? (
      <OrderReviewQuantityRow
        name={productItems[0].name}
        description={productItems[0].subtext}
        quantity={quantity}
        lineTotal={preDiscountSubtotal}
        currency={currency}
        minQuantity={minQuantity}
        maxQuantity={maxQuantity}
        strikethrough={discountAmount > 0}
        onChangeQuantity={onChangeQuantity}
      />
    ) : (
      productItems.map((item) => (
        <OrderItem
          key={item.name}
          name={item.name}
          price={formatPrice(item.price, 2, currency)}
          subtext={item.subtext}
        />
      ))
    )}

    {(additionalItems.length > 0 || showSalesTax || discountAmount > 0) && <hr className='margin-all-xs' />}

    {additionalItems.length > 0 && (
      <>
        {additionalItems.map((item) => (
          <OrderItem
            key={item.name}
            name={item.name}
            price={formatPrice(item.price, 2, currency)}
            strikethrough={item.waived}
            nameSuffix={
              item.waived && (
                <span className='order-review-waived-badge' data-ref='buy.order-review.summary.waived-tag'>
                  {t((item.waivedLabelKey ?? 'client.order-review.waived-tag') as TranslationKey)}
                </span>
              )
            }
          />
        ))}

        {(showSalesTax || discountAmount > 0) && <hr className='margin-all-xs' />}
      </>
    )}

    {showSalesTax && (
      <OrderItem
        name={t('client.order-review.sales-tax')}
        price={getSalestaxDisplayText(salesTax)}
        subtext={t('client.order-review.sales-tax.subtext')}
      />
    )}

    {discountAmount > 0 && (
      <Discount currency={currency} discountAmount={discountAmount} discountName={discountName} subtotal={total} />
    )}

    {/* Only needed when the sales tax row or the discount row actually rendered above — otherwise
        this would sit directly against the additionalItems/product-items hr above with nothing
        between them, showing as a redundant blank divider line. */}
    {(showSalesTax || discountAmount > 0) && <hr className='margin-all-xs' />}

    <OrderItem
      name={t('client.order-review.total-due-today')}
      price={formatPrice(total, 2, currency)}
      nameStyle={{ fontSize: 'var(--font-size-md)', fontWeight: 'var(--font-weight-normal)' }}
    />

    {showTierFinePrint && tier?.finePrint && (
      <SRow className='pad-top-xs'>
        <SCol>
          <p className='text-sm' data-ref='buy.order-review.summary.fine-print'>
            {tier.finePrint}
          </p>
        </SCol>
      </SRow>
    )}
  </div>
);

export default OrderReviewSummary;
